import { type PropsWithChildren } from 'react';
import { assertDefined } from '@epihub/core/src/fn/assert';
import { _get } from '@epihub/core/src/fn/object';
import { promise } from '@epihub/core/src/fn/promise';
import { expectMobileDeps } from '@epihub/web/src/_ctx/deps';
import { type NotificationData, type PlatformAgnosticSnackbarAPI } from '../snackbar';

// Ref to hold the toast controller for imperative access outside React
let controllerRef: any = null;

// Queue to correlate rendered toasts with their full NotificationData,
// since useToastState only returns { id, title, message, duration }
const notificationQueue: NotificationData[] = [];

export function createMobileSnackbarProvider() {
  const ToastProvider = assertDefined(
    _get(expectMobileDeps().snackbars, 'ToastProvider'),
    '@tamagui/toast missing ToastProvider component',
  );

  const ToastViewport = assertDefined(
    _get(expectMobileDeps().snackbars, 'ToastViewport'),
    '@tamagui/toast missing ToastViewport component',
  );

  const Toast = assertDefined(
    _get(expectMobileDeps().snackbars, 'Toast'),
    '@tamagui/toast missing Toast component',
  );

  const useToastController = assertDefined(
    _get(expectMobileDeps().snackbars, 'useToastController'),
    '@tamagui/toast missing useToastController hook',
  );

  const useToastState = assertDefined(
    _get(expectMobileDeps().snackbars, 'useToastState'),
    '@tamagui/toast missing useToastState hook',
  );

  // Bridge component that captures the toast controller into the ref
  function ControllerBridge() {
    const controller = useToastController();
    controllerRef = controller;
    return null;
  }

  // Component that renders the current toast from the toast stack
  function CurrentToast() {
    const currentToast = useToastState();
    if (!currentToast || currentToast.isHandledNatively) {
      return null;
    }

    const notification = notificationQueue[0];

    return (
      <Toast
        key={currentToast.id}
        duration={currentToast.duration}
        background={notification?.color}
        style={notification?.style}
        onOpenChange={(open: boolean) => {
          if (!open) {
            const removed = notificationQueue.shift();
            removed?.onClose?.(removed);
          }
        }}
      >
        {notification?.icon}
        {(notification?.title ?? currentToast.title) && (
          <Toast.Title>{notification?.title ?? currentToast.title}</Toast.Title>
        )}
        {(notification?.message ?? currentToast.message) && (
          <Toast.Description>{notification?.message ?? currentToast.message}</Toast.Description>
        )}
        <Toast.Action asChild altText='Dismiss'>
          <Toast.Close />
        </Toast.Action>
      </Toast>
    );
  }

  return ({ children }: PropsWithChildren) => (
    <ToastProvider>
      <ControllerBridge />
      <CurrentToast />
      {children}
      <ToastViewport />
    </ToastProvider>
  );
}

export function createMobileSnackbars() {
  return {
    show(notification: NotificationData) {
      return promise((resolve) => {
        if (!controllerRef) {
          resolve();
          return;
        }
        notificationQueue.push(notification);
        controllerRef.show(
          typeof notification.title === 'string' ? notification.title : '',
          {
            message: typeof notification.message === 'string' ? notification.message : '',
            duration: typeof notification.autoClose === 'number' ? notification.autoClose : undefined,
          },
        );
        notification?.onOpen?.(notification);
        resolve();
      });
    },
    update(id: string, notification: Omit<NotificationData, 'id'>) {
      // Tamagui toast doesn't support in-place updates; hide current and show new
      controllerRef?.hide();
      notificationQueue.push(notification as NotificationData);
      controllerRef?.show(
        typeof notification.title === 'string' ? notification.title : '',
        {
          message: typeof notification.message === 'string' ? notification.message : '',
          duration: typeof notification.autoClose === 'number' ? notification.autoClose : undefined,
        },
      );
    },
    hide(_id: string) {
      controllerRef?.hide();
    },
    clearAll() {
      while (notificationQueue.length > 0) {
        const removed = notificationQueue.shift();
        removed?.onClose?.(removed);
      }
      controllerRef?.hide();
    },
  } satisfies PlatformAgnosticSnackbarAPI;
}
